/**
 * Socket.IO client connection management
 *
 * The socket is a singleton for the app lifetime. Listener registration helpers
 * keep at most ONE handler per event (keyed by event name), so React StrictMode
 * remounts or repeated hook mounts can never pile up duplicate listeners.
 */

import { io, Socket } from 'socket.io-client';
import type { ClientEvent, ServerEvent } from '../types/generated';

const SOCKET_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';

let socket: Socket | null = null;

// One registered handler per socket / manager event
type AnyHandler = (...args: never[]) => void;
const socketHandlers = new Map<string, AnyHandler>();
const managerHandlers = new Map<string, AnyHandler>();

export function getSocket(): Socket {
  if (!socket) {
    socket = io(SOCKET_URL, {
      transports: ['websocket'],  // WebSocket only, no polling fallback
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    // Connection event logging (dev only)
    if (import.meta.env.DEV) {
      socket.on('connect', () => {
        console.log('Connected to server');
      });

      socket.on('disconnect', (reason) => {
        console.log('Disconnected:', reason);
      });
    }

    socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
    });
  }

  return socket;
}

export function connectSocket(): void {
  const s = getSocket();
  if (!s.connected) {
    s.connect();
  }
}

export function disconnectSocket(): void {
  if (socket?.connected) {
    socket.disconnect();
  }
}

export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

export function sendMessage(event: ClientEvent): boolean {
  const s = getSocket();
  if (s.connected) {
    s.emit('message', event);
    return true;
  } else {
    console.warn('Socket not connected, cannot send message:', event.type);
    return false;
  }
}

/**
 * Measure round-trip latency using the server's 'ping' ack handler.
 * Calls onResult with the RTT in ms, or never if the ack times out.
 */
export function measureLatency(onResult: (ms: number) => void, timeoutMs = 5000): void {
  const s = getSocket();
  if (!s.connected) return;
  const start = performance.now();
  s.timeout(timeoutMs).emit('ping', (err: Error | null) => {
    if (err) return; // No ack within timeout - skip this sample
    onResult(Math.round(performance.now() - start));
  });
}

/** Register a single handler for a socket event, replacing any previous one. */
function bindSocketEvent<A extends unknown[]>(event: string, handler: (...args: A) => void): () => void {
  const s = getSocket();
  const existing = socketHandlers.get(event);
  if (existing) {
    s.off(event, existing as (...args: unknown[]) => void);
  }
  socketHandlers.set(event, handler as unknown as AnyHandler);
  s.on(event, handler as (...args: unknown[]) => void);

  return () => {
    s.off(event, handler as (...args: unknown[]) => void);
    if (socketHandlers.get(event) === (handler as unknown as AnyHandler)) {
      socketHandlers.delete(event);
    }
  };
}

/** Register a single handler for a Manager (reconnection) event, replacing any previous one. */
function bindManagerEvent<A extends unknown[]>(event: string, handler: (...args: A) => void): () => void {
  const mgr = getSocket().io;
  const existing = managerHandlers.get(event);
  if (existing) {
    mgr.off(event as never, existing as never);
  }
  managerHandlers.set(event, handler as unknown as AnyHandler);
  mgr.on(event as never, handler as never);

  return () => {
    mgr.off(event as never, handler as never);
    if (managerHandlers.get(event) === (handler as unknown as AnyHandler)) {
      managerHandlers.delete(event);
    }
  };
}

export function onMessage(handler: (event: ServerEvent) => void): () => void {
  return bindSocketEvent('message', handler);
}

export function onConnect(handler: () => void): () => void {
  return bindSocketEvent('connect', handler);
}

export function onDisconnect(handler: (reason: string) => void): () => void {
  return bindSocketEvent('disconnect', handler);
}

export function onReconnectAttempt(handler: (attempt: number) => void): () => void {
  return bindManagerEvent('reconnect_attempt', handler);
}

export function onReconnect(handler: () => void): () => void {
  return bindManagerEvent('reconnect', handler);
}

export function onReconnectFailed(handler: () => void): () => void {
  return bindManagerEvent('reconnect_failed', handler);
}

export function getConnectionState(): {
  connected: boolean;
  socketId: string | undefined;
} {
  const s = getSocket();
  return {
    connected: s.connected,
    socketId: s.id,
  };
}
